// ──────────────────────────────────────────────────────────
// reels-bar.component.ts
// Angular 14+ Standalone Component (no signals)
// Horizontal reels bar + fullscreen vertical-scroll viewer
// Supports: Pet, Car, Sale2, Required, Teacher, Nadlan, Coworker
// ──────────────────────────────────────────────────────────

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Pipe,
  PipeTransform,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {extractReelDisplay, ReelDisplayData, ReelItem} from "./reels.model";
import { DragScrollDirective } from '../../shared/directives/drag-scroll.directive';


/**
 * Pure pipe — avoids calling extractReelDisplay on every change-detection cycle.
 */
@Pipe({ name: 'reelDisplay', standalone: true, pure: true })
export class ReelDisplayPipe implements PipeTransform {
  transform(reel: ReelItem): ReelDisplayData {
    return extractReelDisplay(reel);
  }
}

@Component({
    selector: 'app-reels-bar',
    imports: [CommonModule, ReelDisplayPipe, DragScrollDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './reels-bar.component.html',
    styleUrls: ['./reels-bar.component.scss']
})
export class ReelsBarComponent implements AfterViewInit, OnDestroy {

  // ─── Inputs ───
  @Input() reels: ReelItem[] = [];
  @Input() title = '';
  @Input() rtl = true;
  @Input() visibleCount = 6;

  // ─── Outputs ───
  @Output() onReelClick  = new EventEmitter<{ reel: ReelItem; index: number }>();
  @Output() onSeeAll     = new EventEmitter<void>();
  @Output() onLike       = new EventEmitter<ReelItem>();
  @Output() onShare      = new EventEmitter<ReelItem>();
  @Output() onContact    = new EventEmitter<ReelItem>();
  @Output() onWhatsapp   = new EventEmitter<ReelItem>();

  // ─── View refs ───
  @ViewChild('track')        trackRef!: ElementRef<HTMLDivElement>;
  @ViewChild('viewerScroll') viewerScrollRef!: ElementRef<HTMLDivElement>;

  // ─── State ───
  activeIndex   = 0;
  viewerOpen    = false;
  viewerVisible = false;
  isMuted       = true;
  pausedSlide: number | null = null;
  // The homepage should expose the complete Stories feed immediately.
  // Users can still collapse it with the existing "show less" control.
  isExpanded = true;

  get displayedReels(): ReelItem[] {
    return this.isExpanded ? this.reels : this.reels.slice(0, this.visibleCount);
  }

  toggleSeeAll(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) this.onSeeAll.emit();
    this.cdr.markForCheck();
    setTimeout(() => this.checkBarScroll());
  }

  // ─── Scroll state ───
  canScrollPrev = false;
  canScrollNext = false;

  private observer: IntersectionObserver | null = null;
  private scrollTimer: any;

  constructor(private cdr: ChangeDetectorRef) {}

  // ── Lifecycle ──

  ngAfterViewInit(): void {
    this.checkBarScroll();
    this.trackRef?.nativeElement?.addEventListener('scroll', () => this.checkBarScroll());
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.scrollTimer) clearTimeout(this.scrollTimer);
  }

  // ── Keyboard ──

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.viewerOpen) this.closeViewer();
  }

  // ── Helpers ──

  roundRating(rating: number | null | undefined): number {
    return Math.floor(rating || 0);
  }

  // ── Track helpers ──

  trackById(_: number, reel: ReelItem): number {
    return reel.id;
  }

  checkBarScroll(): void {
    const el = this.trackRef?.nativeElement;
    if (!el) return;
    const scrollPos = Math.abs(el.scrollLeft);
    this.canScrollPrev = scrollPos > 10;
    this.canScrollNext = scrollPos < el.scrollWidth - el.clientWidth - 10;
    this.cdr.markForCheck();
  }

  scrollBarBy(direction: number): void {
    const el = this.trackRef?.nativeElement;
    if (!el) return;

    // Measure the real card + gap rather than hardcoding them. The card width
    // and gap are both CSS custom properties that change at the mobile
    // breakpoint, so a hardcoded step drifts out of sync with what's actually
    // on screen (and silently broke every time either value was tuned).
    const card = el.querySelector('.thumb') as HTMLElement | null;
    const cardWidth = card?.offsetWidth ?? 144;
    const gap = parseFloat(getComputedStyle(el).columnGap || '0') || 0;
    const step = (cardWidth + gap) * 3;

    const amount = this.rtl ? -step * direction : step * direction;
    el.scrollBy({ left: amount, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
  }

  /** Respects the OS "reduce motion" setting for programmatic scrolling. */
  private get prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  }

  // ── Thumbnail hover preview ──

  /**
   * Videos without an explicit poster image default to their frame at t=0,
   * which is very often a blank/black flash-frame. Once metadata is ready,
   * seek to ~1s so the resting thumbnail shows real content instead. Skipped
   * when a real poster image was supplied — that already takes priority
   * while paused, no need to touch the video's own frame.
   */
  onThumbMetadataLoaded(e: Event, reel: ReelItem): void {
    if (reel.images?.length) return;
    const v = e.target as HTMLVideoElement;
    if (!isFinite(v.duration) || v.duration <= 0) return;
    v.currentTime = Math.min(1, v.duration / 2);
  }

  hoverPlay(e: MouseEvent): void {
    const v = e.target as HTMLVideoElement;
    v.play().catch(() => {});
  }

  hoverPause(e: MouseEvent): void {
    const v = e.target as HTMLVideoElement;
    v.pause();
    if (isFinite(v.duration) && v.duration > 0) {
      v.currentTime = Math.min(1, v.duration / 2);
    }
  }

  // ── Viewer open / close ──

  openViewer(index: number): void {
    this.activeIndex = index;
    this.viewerOpen = true;
    this.pausedSlide = null;
    this.onReelClick.emit({ reel: this.reels[index], index });

    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.viewerVisible = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.scrollToSlide(index, 'auto');
        this.initObserver();
      }, 60);
    });
  }

  closeViewer(): void {
    this.viewerVisible = false;
    this.pauseAll();
    this.cdr.markForCheck();
    setTimeout(() => {
      this.viewerOpen = false;
      document.body.style.overflow = '';
      this.observer?.disconnect();
      this.cdr.markForCheck();
    }, 300);
  }

  // ── Play / pause / mute ──

  togglePlay(index: number): void {
    const video = this.getSlideVideo(index);
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      this.pausedSlide = null;
    } else {
      video.pause();
      this.pausedSlide = index;
    }
    this.cdr.markForCheck();
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;
    this.cdr.markForCheck();
  }

  // ── Scroll / snap ──

  scrollToSlide(index: number, behavior: ScrollBehavior = 'smooth'): void {
    const el = this.viewerScrollRef?.nativeElement;
    if (!el) return;
    el.scrollTo({ top: el.clientHeight * index, behavior });
  }

  onViewerScroll(): void {
    if (this.scrollTimer) clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      const el = this.viewerScrollRef?.nativeElement;
      if (!el) return;
      const idx = Math.round(el.scrollTop / el.clientHeight);
      if (idx !== this.activeIndex) {
        this.activeIndex = idx;
        this.pausedSlide = null;
        this.cdr.markForCheck();
      }
    }, 80);
  }

  // ── IntersectionObserver — auto play / pause ──

  private initObserver(): void {
    this.observer?.disconnect();
    const root = this.viewerScrollRef?.nativeElement;
    if (!root) return;

    this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = (entry.target as HTMLElement).querySelector('video');
            if (!video) return;
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { root, threshold: [0.6] }
    );

    root.querySelectorAll('.vw__slide').forEach((slide) => {
      this.observer!.observe(slide);
    });
  }

  private pauseAll(): void {
    const root = this.viewerScrollRef?.nativeElement;
    if (!root) return;
    root.querySelectorAll('video').forEach((v: HTMLVideoElement) => v.pause());
  }

  private getSlideVideo(index: number): HTMLVideoElement | null {
    const root = this.viewerScrollRef?.nativeElement;
    if (!root) return null;
    const slide = root.querySelector(`.vw__slide[data-index="${index}"]`);
    return slide?.querySelector('video') || null;
  }
}
